import { reorderArrayItems } from "@/features/survey/utils/reducerFactory";
import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import type {
  SurveyPOI,
  SurveyPOICategory,
  SurveyPOIWithCategory,
} from "./types/survey.poi.types";

export interface POIsState {
  items: SurveyPOIWithCategory[];
  categories: SurveyPOICategory[];
  isLoading: boolean;
  error: string | null;
}

const initialState: POIsState = {
  items: [],
  categories: [],
  isLoading: false,
  error: null,
};

const poisSlice = createSlice({
  name: "pois",
  initialState,
  reducers: {
    setPOIs: (state, action: PayloadAction<SurveyPOI[]>) => {
      const categoryMap = new Map(state.categories.map((c) => [c.extId, c]));
      state.items = action.payload.map((poi, index) => ({
        ...poi,
        id: poi.extId,
        order: poi.order ?? index,
        isHidden: poi.isHidden ?? false,
        category: poi.categoryId ? categoryMap.get(poi.categoryId) : undefined,
      }));
      state.error = null;
    },

    addCategory: (state, action: PayloadAction<SurveyPOICategory>) => {
      state.categories.push(action.payload);
    },

    setCategories: (state, action: PayloadAction<SurveyPOICategory[]>) => {
      // Build maps for old and new categories to detect changes using value equality.
      // API responses often return new object instances even when the data is unchanged,
      // so reference equality would always treat them as different.
      const oldCategoryMap = new Map(state.categories.map((c) => [c.extId, c]));
      const newCategoryMap = new Map(action.payload.map((c) => [c.extId, c]));

      state.categories = action.payload;

      state.items = state.items.map((poi) => {
        if (!poi.categoryId) return poi;

        const oldCat = oldCategoryMap.get(poi.categoryId);
        const newCat = newCategoryMap.get(poi.categoryId);

        const categoriesMatch =
          oldCat &&
          newCat &&
          JSON.stringify(oldCat) === JSON.stringify(newCat);

        if (categoriesMatch) return poi;

        return { ...poi, category: newCat };
      });
    },

    addPOI: (state, action: PayloadAction<SurveyPOI>) => {
      const categoryMap = new Map(state.categories.map((c) => [c.extId, c]));
      state.items.push({
        ...action.payload,
        id: action.payload.extId,
        order: state.items.length,
        isHidden: false,
        category: action.payload.categoryId
          ? categoryMap.get(action.payload.categoryId)
          : undefined,
      });
    },

    updatePOI: (
      state,
      action: PayloadAction<{ extId: string; updates: Partial<SurveyPOI> }>,
    ) => {
      const idx = state.items.findIndex(
        (p) => p.extId === action.payload.extId,
      );
      if (idx !== -1) {
        const updated = { ...state.items[idx], ...action.payload.updates };
        if (action.payload.updates.categoryId) {
          const categoryMap = new Map(
            state.categories.map((c) => [c.extId, c]),
          );
          updated.category = categoryMap.get(action.payload.updates.categoryId);
        }
        state.items[idx] = updated;
      }
    },

    removePOI: (state, action: PayloadAction<string>) => {
      state.items = state.items
        .filter((p) => p.extId !== action.payload)
        .map((p, i) => ({ ...p, order: i }));
    },

    removeCategory: (state, action: PayloadAction<string>) => {
      state.categories = state.categories.filter(
        (c) => c.extId !== action.payload,
      );
    },

    togglePOIVisibility: (state, action: PayloadAction<string>) => {
      const idx = state.items.findIndex((p) => p.extId === action.payload);
      if (idx !== -1) {
        state.items[idx].isHidden = !state.items[idx].isHidden;
      }
    },

    reorderPOIs: (
      state,
      action: PayloadAction<{
        sourceIndex: number;
        destinationIndex: number;
      }>,
    ) => {
      const { sourceIndex, destinationIndex } = action.payload;
      state.items = reorderArrayItems(
        state.items,
        sourceIndex,
        destinationIndex,
      );
    },

    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    /**
     * Optimistically apply a partial update to a category — used by PoiDesignForm
     * for instant preview of color / icon / size / pinType changes.
     */
    updateCategoryField: (
      state,
      action: PayloadAction<{
        categoryId: string;
        updates: Partial<SurveyPOICategory>;
      }>,
    ) => {
      const { categoryId, updates } = action.payload;
      const idx = state.categories.findIndex((c) => c.extId === categoryId);
      if (idx !== -1) {
        state.categories[idx] = {
          ...state.categories[idx],
          ...updates,
          // Deep-merge iconData so callers can update color or iconName independently
          ...(updates.iconData && {
            iconData: {
              ...state.categories[idx].iconData,
              ...updates.iconData,
            },
          }),
        };
        const updatedCategory = state.categories[idx];
        // Propagate to all POIs that reference this category
        state.items = state.items.map((poi) =>
          poi.categoryId === categoryId
            ? { ...poi, category: updatedCategory }
            : poi,
        );
      }
    },
  },
});

export const {
  setPOIs,
  setCategories,
  addCategory,
  addPOI,
  updatePOI,
  updateCategoryField,
  removePOI,
  removeCategory,
  togglePOIVisibility,
  reorderPOIs,
  setLoading,
  setError,
} = poisSlice.actions;

export default poisSlice.reducer;
